import { ApproximateLocationMap } from 'app/components/approximate-location-map';
import { FavouriteButton } from 'app/components/favourite-button';
import { InsuredUser, useInsuredUsers } from 'app/components/insured-users';
import LenderAcceptedLoan from 'app/components/lender-accepted-loan';
import { ListingHero } from 'app/components/listing-hero';
import { ToastProvider } from 'app/components/toast-provider';
import { ShareAndroid } from 'app/design/icons/shareAndroid';
import { ShareApple } from 'app/design/icons/shareApple';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { PCImage } from 'app/design/primitives/image';
import { Input } from 'app/design/primitives/input';
import { RemoteError } from 'app/design/primitives/remote-error';
import { Text } from 'app/design/primitives/text';
import { localeAwareFormat, localeAwareFormatDurationShort } from 'app/lib/date';
import { SUPPORT_CHANNEL_URL } from 'app/network/chat/channel';
import { useListingBorrowers, useReportListing } from 'app/network/listing';
import { useCreateListingComment, useDeleteListingComment, useListingComments } from 'app/network/listing-comment';
import { useDeleteRating, useRatings, useReportRating } from 'app/network/loan';
import { useUser } from 'app/network/user';
import { navigateToUserProfile } from 'app/screens/billboard/navigator-helper';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import { LoanFeeModals, useLoanFeeModals, type LoanFeeModalsState } from 'app/screens/management/loan-fee';
import { ListingContext } from 'app/screens/shared/one-listing/components/ListingProvider';
import { PosterSection } from 'app/screens/shared/one-listing/components/poster-section';
import type { Listing, ListingBorrower, ListingComment } from 'app/types/listing';
import { ListingSubtype, ListingType, TransactionMode } from 'app/types/listing';
import { isDonationListing } from 'app/lib/listing';
import { FeatureType } from 'app/types/recommender';
import { FeatureFlag, isBlank, isFeatureEnabled, isWhiteLabel } from 'app/utils/helpers';
import { useAuthorize } from 'app/hooks/use-authorize';
import { useIsGuestWeb, useUISize } from 'app/utils/web-helpers';
import React, { useContext, useRef, useState, type ReactElement, type ReactNode, type RefObject } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Alert,
  FlatList,
  Linking,
  Platform,
  Pressable,
  StyleSheet,
  useWindowDimensions,
  View,
  type RefreshControlProps,
  type ScrollView,
  type TextInput,
} from 'react-native';

import {
  IconAlertTriangle,
  IconCalendar,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconChevronUp,
  IconCircleCheck,
  IconClock,
  IconCoin,
  IconCreditCard,
  IconInfoCircle,
  IconPackage,
  IconSend,
  IconTag,
  IconUserCircle,
  IconUsers,
  IconX,
} from '@tabler/icons-react-native';
import { CorpoImage } from 'app/components/corp-image';
import { ListingOfferCard } from 'app/components/listing-offer-card';
import { ListingCardTimeTag } from 'app/components/listing-card-metadata';
import { ListingRequestCard } from 'app/components/listing-request-card';
import { RatingCard } from 'app/components/rating-card';
import { StatusPerishable } from 'app/components/status-perishable';
import { AmbassadorMedal } from 'app/design/icons/ambassador-medal';
import { useAmbassador } from 'app/hooks/use-ambassador';
import { CheckCircle2 } from 'app/design/icons/check-circle2';
import { SuperSharerMedal } from 'app/design/icons/super-sharer-medal';
import { Gradient } from 'app/design/primitives/gradient';
import type { SwiperImage } from 'app/design/primitives/image-swiper';
import { Separator } from 'app/design/primitives/text-divider';
import { useRoute } from 'app/hooks/navigation';
import { getImageVariantUrl } from 'app/lib/image-variants';
import { THUMBS_UP } from 'app/network/chat/message';
import {
  ListingListWithStrategy,
  Strategies,
  useRecommendationRuntime,
  useSimilarListingsRecommendationsWithRuntime,
} from 'app/screens/billboard/all-listings/components/recommended-listings';
import { DesktopRecommendationSection } from 'app/screens/billboard/all-listings/components/recommended-listings.large';
import { createTheme as createDSTheme } from 'app/shopify';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { isSameMonth } from 'date-fns';
import Animated, {
  Extrapolation,
  FadeIn,
  interpolate,
  useAnimatedStyle,
  useReducedMotion,
  type ScrollHandlerProcessed,
  type SharedValue,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getListingAvailability, type ListingAvailabilityPresentation } from './listing-availability';
import { ListingAvailabilityCalendar } from './listing-availability-calendar';
import { showCommentsUI } from './listing-comments';
import { getOfferMarketValue } from './offer-market-value';
import { WhiteLabel } from 'app/types/white-labels';
const theme = createDSTheme();

type NeighboredListingProps = Listing & {
  city?: string;
  onCommentInputFocus?: (input: TextInput | null) => void;
  onCommentInputBlur?: () => void;
  scrollViewRef?: RefObject<ScrollView | null>;
  scrollOffsetRef?: RefObject<number>;
  bottomAccessory?: ReactNode;
  topContent?: ReactNode;
  hasUnavailabilityBanner?: boolean;
  unavailabilityBannerHeight?: number;
  refreshControl?: ReactElement<RefreshControlProps>;
  setPageRef?: (ref: ScrollView | null) => void;
  tabs?: ReactNode;
  scrollY: SharedValue<number>;
  onScroll: ScrollHandlerProcessed;
};

const COMMENT_AVATAR_SIZE = 32;
const COMMENT_MESSAGE_MIN_HEIGHT = 20;
const COMMENTS_ENABLED = false;
const COLLAPSED_HEADER_CONTENT_HEIGHT = 60;
const HERO_MIN_HEIGHT = 320;
const HERO_MAX_HEIGHT = 460;

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <Text color="text.primary" fontWeight="700" variant="headerXSmall">
      {children}
    </Text>
  );
}

function ListingTags({ listing, showPerishable }: { listing: Pick<Listing, 'listingsTags'>; showPerishable: boolean }) {
  if (!listing.listingsTags?.length && !showPerishable) return null;

  return (
    <Row
      alignItems="center"
      columnGap="spacing.general.x1"
      flexShrink={1}
      flexWrap="wrap"
      style={{ rowGap: theme.spacing['spacing.general.x1'] }}
    >
      {showPerishable ? <StatusPerishable compact /> : null}
      {listing.listingsTags?.map((listingTag) => (
        <View key={listingTag.tagId} style={styles.listingTag}>
          <Text color="text.secondary" fontWeight="500" numberOfLines={1} variant="bodySmall">
            {listingTag.tagName}
          </Text>
        </View>
      ))}
    </Row>
  );
}

function formatOfferDetailDate(listing: Pick<Listing, 'end' | 'start'>, t: ReturnType<typeof useTranslation>['t']) {
  const dateFormat = t('dateFormat_MMMMdYYYY', { ns: 'common' });

  const start = listing.start ? new Date(listing.start) : null;
  const end = listing.end ? new Date(listing.end) : null;
  const validStart = start && !Number.isNaN(start.getTime()) ? start : null;
  const validEnd = end && !Number.isNaN(end.getTime()) ? end : null;

  if (validStart && validEnd && validStart.getTime() !== validEnd.getTime()) {
    return `${localeAwareFormat(validStart, dateFormat)} – ${localeAwareFormat(validEnd, dateFormat)}`;
  }

  if (validStart) {
    return localeAwareFormat(validStart, dateFormat);
  }

  if (validEnd) {
    return localeAwareFormat(validEnd, dateFormat);
  }

  return null;
}

function OfferDetailInfoRow({
  icon,
  label,
  value,
  action,
}: {
  icon?: ReactNode;
  label: string;
  value: string;
  action?: ReactNode;
}) {
  return (
    <Row alignItems="center" gap="spacing.general.x3">
      {icon ? <View style={styles.offerDetailIcon}>{icon}</View> : null}
      <Column flex={1} gap="spacing.general.x0,5">
        <Text color="text.secondary" variant="bodySmall">
          {label}
        </Text>
        <Text color="text.primary" fontWeight="600" variant="bodyBase">
          {value}
        </Text>
      </Column>
      {action}
    </Row>
  );
}

function OfferDetailsSection({
  listing,
  loanFeeModalsState,
}: {
  listing: Listing;
  loanFeeModalsState: LoanFeeModalsState;
}) {
  const { t } = useTranslation('one-listing');
  const { data: user } = useUser();
  const { setIsLoanFeeOtherModalOpen } = loanFeeModalsState;
  const transactionModeEnabled = isFeatureEnabled(FeatureFlag.transactionMode, user?.featureFlags);

  const loanFeeEnabled = isFeatureEnabled(FeatureFlag.loanFee, user?.featureFlags);
  const marketValueEnabled = isFeatureEnabled(FeatureFlag.listingPromptEstimatedPrice, user?.featureFlags);

  const rows = (() => {
    const result: ReactNode[] = [];
    const mode = listing.transactionMode ?? TransactionMode.lend;

    if (transactionModeEnabled && listing.transactionMode) {
      const modeType = listing.type === ListingType.request ? 'request' : 'offer';
      result.push(
        <OfferDetailInfoRow
          key="transactionMode"
          icon={<IconTag strokeWidth={ICON_STROKE_WIDTH} size={18} color={theme.colors['icon.secondary']} />}
          label={t('headerSection.transactionModeLabel')}
          value={t(`headerSection.transactionMode.${modeType}.${listing.transactionMode}`)}
        />,
      );
    }

    if (transactionModeEnabled && mode === TransactionMode.sell && listing.price) {
      result.push(
        <OfferDetailInfoRow
          key="price"
          icon={<IconCoin strokeWidth={ICON_STROKE_WIDTH} size={18} color={theme.colors['icon.secondary']} />}
          label={t('headerSection.price')}
          value={listing.price}
        />,
      );
    }

    if (transactionModeEnabled && listing.inventory?.trim()) {
      result.push(
        <OfferDetailInfoRow
          key="inventory"
          icon={<IconPackage strokeWidth={ICON_STROKE_WIDTH} size={18} color={theme.colors['icon.secondary']} />}
          label={t('headerSection.inventory')}
          value={listing.inventory}
        />,
      );
    }

    const marketValue = getOfferMarketValue({
      estimatedPrice: listing.estimatedPrice,
      marketValueEnabled,
      transactionMode: mode,
    });

    if (listing.type === ListingType.offer && marketValue) {
      result.push(
        <OfferDetailInfoRow
          key="marketValue"
          icon={<IconCoin strokeWidth={ICON_STROKE_WIDTH} size={18} color={theme.colors['icon.secondary']} />}
          label={t('headerSection.marketValue')}
          value={t('x$', { ns: 'common', amount: marketValue.value.toFixed(2) })}
        />,
      );
    }

    if (loanFeeEnabled && listing.loanFee !== null) {
      result.push(
        <OfferDetailInfoRow
          key="sharingFee"
          icon={<IconCoin strokeWidth={ICON_STROKE_WIDTH} size={18} color={theme.colors['icon.secondary']} />}
          label={t('headerSection.sharingFee')}
          value={t('x$', { ns: 'common', amount: listing.loanFee?.toFixed(2) })}
          action={
            <Pressable
              accessibilityLabel={t('modals.loanFee.title', { ns: 'loanFee' })}
              accessibilityRole="button"
              hitSlop={8}
              onPress={() => setIsLoanFeeOtherModalOpen(true)}
            >
              <IconInfoCircle strokeWidth={ICON_STROKE_WIDTH} size={20} color={theme.colors['icon.secondary']} />
            </Pressable>
          }
        />,
      );
    }

    if (loanFeeEnabled && listing.loanFee !== null && listing.loanFeePaymentMethod) {
      result.push(
        <OfferDetailInfoRow
          key="paymentMethod"
          icon={<IconCreditCard strokeWidth={ICON_STROKE_WIDTH} size={18} color={theme.colors['icon.secondary']} />}
          label={t('fields.loanFeePaymentMethodLabel', { ns: 'loanFee' })}
          value={t(`paymentMethods.${listing.loanFeePaymentMethod}`, { ns: 'loanFee' })}
        />,
      );
    }

    if (listing.type === ListingType.offer) {
      const dateValue = formatOfferDetailDate(listing, t);
      if (dateValue) {
        const start = listing.start ? new Date(listing.start) : null;
        const end = listing.end ? new Date(listing.end) : null;
        const isDateRange =
          start !== null &&
          end !== null &&
          !Number.isNaN(start.getTime()) &&
          !Number.isNaN(end.getTime()) &&
          start.getTime() !== end.getTime();
        result.push(
          <OfferDetailInfoRow
            key="date"
            icon={<IconCalendar strokeWidth={ICON_STROKE_WIDTH} size={18} color={theme.colors['icon.secondary']} />}
            label={t(isDateRange ? 'headerSection.dates' : 'headerSection.date').replace(/\s*:\s*$/, '')}
            value={dateValue}
          />,
        );
      }
    } else {
      result.push(<RequestDateTag listing={listing} />);
    }

    return result;
  })();

  if (rows.length === 0) return null;

  return (
    <View style={styles.detailCard}>
      <Column gap="spacing.general.x3">{rows}</Column>
    </View>
  );
}

function RequestDateTag({ listing }: { listing: Listing }) {
  const { t } = useTranslation('all-listings');
  const { start, end } = listing;

  const dateText = (() => {
    if (start && end) {
      return start === end
        ? localeAwareFormat(new Date(start), 'd MMM')
        : t('dateRange', {
            start: isSameMonth(new Date(start), new Date(end))
              ? new Date(start).getDate()
              : localeAwareFormat(new Date(start), 'd MMM'),
            end: localeAwareFormat(new Date(end), 'd MMM'),
          });
    }
    return t('noDateRange');
  })();

  return (
    <Row alignSelf="flex-start" gap="spacing.general.x1" alignItems="center" style={styles.requestDateTag}>
      <IconCalendar strokeWidth={ICON_STROKE_WIDTH} size={14} color={theme.colors['text.primary']} />
      <Text variant="bodySmall">{dateText}</Text>
    </Row>
  );
}

function BorrowerAvatar({
  borrower,
  size,
}: {
  borrower: Pick<ListingBorrower, 'isAmbassador' | 'isSuper' | 'picture'>;
  size: number;
}) {
  const ambassadorEnabled = useAmbassador().isEnabled;
  const badgeWidth = Math.round(size * 0.63);
  const badgeStyle = [
    styles.borrowerBadge,
    {
      bottom: -Math.max(1, Math.round(size * 0.12)),
      right: Math.max(1, Math.round(size * 0.04)),
    },
  ];

  return (
    <View style={{ height: size + 4, width: size + 4 }}>
      <PCImage
        height={size}
        width={size}
        source={borrower.picture ? { uri: getImageVariantUrl(borrower.picture, 'mini') } : undefined}
        style={[styles.borrowerAvatar, { borderRadius: size / 2 }]}
        useOwnCache
      />

      {ambassadorEnabled && borrower.isAmbassador ? (
        <AmbassadorMedal height={badgeWidth * (60 / 57)} width={badgeWidth} style={badgeStyle} />
      ) : borrower.isSuper ? (
        <SuperSharerMedal height={badgeWidth * (69 / 57)} width={badgeWidth} style={badgeStyle} />
      ) : null}
    </View>
  );
}

function BorrowerCard({ borrower }: { borrower: ListingBorrower }) {
  const { navigation } = useContext(ListingContext);
  const { t } = useTranslation('one-listing');

  return (
    <Pressable
      accessibilityRole="button"
      onPress={() => navigateToUserProfile(navigation, borrower.id)}
      style={styles.borrowerCard}
    >
      <BorrowerAvatar borrower={borrower} size={64} />
      <Text fontWeight="700" numberOfLines={2} textAlign="center" variant="bodySmall">
        {borrower.firstName} {borrower.lastName}
      </Text>
      <Text color="text.placeholder" numberOfLines={1} textAlign="center" variant="bodySmall">
        {t('borrowers.borrowedAgo', {
          ago: localeAwareFormatDurationShort(new Date(borrower.lastBorrowedAt)),
        })}
      </Text>
    </Pressable>
  );
}

function CurrentBorrowerSection({ borrower }: { borrower: ListingBorrower }) {
  const { navigation } = useContext(ListingContext);
  const { t } = useTranslation('one-listing');
  const plannedEnd = borrower.currentLoanEnd ? new Date(borrower.currentLoanEnd) : null;
  const hasValidPlannedEnd = plannedEnd && !Number.isNaN(plannedEnd.getTime());
  const borrowerName = [borrower.firstName, borrower.lastName].filter(Boolean).join(' ');

  return (
    <Pressable
      accessibilityLabel={t('currentBorrower.viewProfile', { name: borrowerName })}
      accessibilityRole="button"
      onPress={() => navigateToUserProfile(navigation, borrower.id)}
      style={({ pressed }) => [styles.currentBorrowerSection, pressed && styles.currentBorrowerSectionPressed]}
    >
      <BorrowerAvatar borrower={borrower} size={58} />

      <View style={styles.currentBorrowerDetails}>
        <Text color="text.secondary" fontWeight="600" variant="bodySmall">
          {t('currentBorrower.title')}
        </Text>
        <Text color="text.primary" fontWeight="700" numberOfLines={1} variant="bodyLarge">
          {borrowerName}
        </Text>
        {hasValidPlannedEnd ? (
          <Row alignItems="center" gap="spacing.general.x1">
            <IconClock color={theme.colors['text.secondary']} size={16} strokeWidth={ICON_STROKE_WIDTH} />
            <Text color="text.secondary" variant="bodySmall">
              {t('currentBorrower.scheduledEnd', {
                date: localeAwareFormat(plannedEnd, t('dateFormat_MMMMdYYYY', { ns: 'common' })),
              })}
            </Text>
          </Row>
        ) : null}
      </View>

      <View style={styles.currentBorrowerChevron}>
        <IconChevronRight color={theme.colors['text.secondary']} size={22} strokeWidth={ICON_STROKE_WIDTH} />
      </View>
    </Pressable>
  );
}

function FloatingHeroControl({
  accessibilityLabel,
  children,
  onPress,
}: {
  accessibilityLabel: string;
  children: ReactNode;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      hitSlop={8}
      onPress={onPress}
      style={styles.floatingControl}
    >
      {children}
    </Pressable>
  );
}

function HeroCorporateBadge({ listing, visible }: { listing: Listing; visible: boolean }) {
  if (!visible || !listing.groups?.length) return null;

  return (
    <CorpoImage
      picture={listing.groups[0]?.picture}
      style={{
        position: 'absolute',
        bottom: theme.spacing['spacing.general.x3'],
        right: theme.spacing['spacing.general.x3'],
        zIndex: 999,
      }}
    />
  );
}

function HeroControls({
  controlsStyle,
  listing,
  onSharePress,
  top,
  visible,
}: {
  controlsStyle: ReturnType<typeof useAnimatedStyle>;
  listing: Listing;
  onSharePress: () => Promise<void>;
  top: number;
  visible: boolean;
}) {
  const { isAuthor } = useContext(ListingContext);
  const { t } = useTranslation();
  if (!visible) return null;

  const favouriteCount = Number.isInteger(listing.numFavourites) ? listing.numFavourites : undefined;

  return (
    <Animated.View pointerEvents="box-none" style={[styles.heroControls, { top }, controlsStyle]}>
      <Row gap="spacing.general.x2" style={{ marginLeft: 'auto' }}>
        <FloatingHeroControl
          accessibilityLabel={t('shareListing', { ns: 'accessibility' })}
          onPress={() => void onSharePress()}
        >
          {Platform.OS === 'ios' ? <ShareApple /> : <ShareAndroid />}
        </FloatingHeroControl>
        {!isAuthor || (favouriteCount ?? 0) > 0 ? (
          <View
            style={[
              styles.floatingControl,
              favouriteCount === undefined ? undefined : styles.favouriteControlWithCount,
            ]}
          >
            <FavouriteButton
              disabled={isAuthor}
              {...(isAuthor ? { isFavouriteForced: true } : {})}
              count={favouriteCount}
              listing={listing}
            />
          </View>
        ) : null}
        {listing.numLoans && listing.serviceCategory?.name !== 'public_tessier' ? (
          <Row
            style={[
              { gap: theme.spacing['spacing.general.x1'] },
              styles.floatingControl,
              favouriteCount === undefined ? undefined : styles.favouriteControlWithCount,
            ]}
          >
            <Text color="text.primary" fontSize={20} style={styles.heroSecondaryText} variant="bodySmall">
              🤝
            </Text>
            <Text color="text.primary" fontSize={16} style={styles.heroSecondaryText} variant="bodySmall">
              {listing.numLoans}
            </Text>
          </Row>
        ) : null}
      </Row>
    </Animated.View>
  );
}

function HeroInformation({
  availability,
  listing,
  overlayStyle,
  posterPartialPostalCode,
  visible,
}: {
  availability: ListingAvailabilityPresentation;
  listing: Listing;
  overlayStyle: ReturnType<typeof useAnimatedStyle>;
  posterPartialPostalCode?: string;
  visible: boolean;
}) {
  if (!visible) return null;

  const isSaleOrDonation = listing.transactionMode === TransactionMode.sell || isDonationListing(listing);

  return (
    <Animated.View pointerEvents="box-none" style={[styles.heroInformation, overlayStyle]}>
      <Text color="surface.default" fontWeight="700" numberOfLines={3} style={styles.heroTitle} variant="headerMedium">
        {listing.item.name}
      </Text>
      <Row>
        {listing.type === ListingType.offer ? (
          isSaleOrDonation ? (
            <ListingCardTimeTag listing={listing} />
          ) : (
            <AvailabilityChip availability={availability} onHero />
          )
        ) : null}
      </Row>

      <PosterSection {...listing} hideGroup hideLocation onHero partialPostalCode={posterPartialPostalCode} />
    </Animated.View>
  );
}

function getAvailabilityLabel(
  availability: ListingAvailabilityPresentation,
  t: ReturnType<typeof useTranslation>['t'],
) {
  const dateFormat = t('dateFormat_MMMMd', { ns: 'common' });

  if (availability.kind === 'borrowed' || availability.kind === 'reserved') {
    return t(availability.labelKey, {
      date: localeAwareFormat(availability.date, dateFormat),
    });
  }

  if (availability.kind === 'request') {
    if (availability.labelKey === 'availability.requestDate' && availability.start) {
      return t(availability.labelKey, {
        date: localeAwareFormat(availability.start, dateFormat),
      });
    }

    if (availability.labelKey === 'availability.requestDateRange' && availability.start && availability.end) {
      return t(availability.labelKey, {
        start: localeAwareFormat(availability.start, dateFormat),
        end: localeAwareFormat(availability.end, dateFormat),
      });
    }
  }

  return t(availability.labelKey);
}

function AvailabilityChip({
  availability,
  onHero = false,
}: {
  availability: ListingAvailabilityPresentation;
  onHero?: boolean;
}) {
  const { t } = useTranslation('one-listing');
  const reduceMotion = useReducedMotion();
  const isAvailable = availability.kind === 'available';
  const Icon = isAvailable ? IconCircleCheck : availability.kind === 'request' ? IconCalendar : IconClock;
  const label = getAvailabilityLabel(availability, t);

  return (
    <Animated.View
      entering={reduceMotion ? undefined : FadeIn.duration(260)}
      style={[
        styles.availabilityChip,
        {
          backgroundColor: onHero
            ? 'rgba(255,255,255,0.94)'
            : isAvailable
              ? theme.colors['surface.success.subtle']
              : theme.colors['surface.tertiary-neutral'],
        },
      ]}
    >
      <Icon
        strokeWidth={ICON_STROKE_WIDTH}
        size={16}
        color={isAvailable ? theme.colors['text.positive'] : theme.colors['text.primary']}
      />

      <Text
        color={isAvailable ? 'text.positive' : 'text.primary'}
        fontWeight="700"
        numberOfLines={1}
        variant="bodySmall"
      >
        {label}
      </Text>
      {availability.kind === 'borrowed' && availability.availableInDays ? (
        <View style={styles.availabilityDetails}>
          <Text color="text.secondary" variant="bodyCaption">
            {t('one-listing:availability.availableIn', { count: availability.availableInDays })}
          </Text>
        </View>
      ) : null}
    </Animated.View>
  );
}

function CommentAvatar({ picture, size = COMMENT_AVATAR_SIZE }: { picture?: string | null; size?: number }) {
  return picture ? (
    <PCImage source={{ uri: picture }} style={{ width: size, height: size, borderRadius: size / 2 }} />
  ) : (
    <View
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        backgroundColor: theme.colors['surface.default'],
        borderWidth: 1,
        borderColor: theme.colors['border.secondary'],
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <IconUserCircle
        strokeWidth={ICON_STROKE_WIDTH}
        color={theme.colors['text.primary']}
        size={Math.round(size * 0.7)}
      />
    </View>
  );
}

function CommentThreadItem({
  comment,
  isOwnComment,
  onDelete,
  withBorderBottom,
}: {
  comment: ListingComment;
  isOwnComment: boolean;
  onDelete: (commentId: string) => void;
  withBorderBottom?: boolean;
}) {
  const [deleteHovered, setDeleteHovered] = useState(false);

  return (
    <Column
      flex={1}
      style={{
        borderRadius: theme.borderRadii['radius.lg'],
        borderBottomWidth: withBorderBottom ? 0 : 1,
        borderColor: theme.colors['border.secondary'],
        paddingLeft: theme.spacing['spacing.general.x5'],
        paddingRight: theme.spacing['spacing.general.x4'],
        paddingVertical: theme.spacing['spacing.general.x3'],
        position: 'relative',
      }}
    >
      <Column style={{ paddingRight: isOwnComment ? 28 : 0 }}>
        <Row alignItems="flex-start" gap="spacing.general.x1" style={{ flex: 1, flexWrap: 'wrap' }}>
          <CommentAvatar picture={comment.user.profile.picture} />
          <Column>
            <Text variant="bodyBase" fontWeight="700">
              {`${comment.user.profile.firstName} ${comment.user.profile.lastName}`}
            </Text>
            <Text color="text.secondary" variant="bodyBase" style={{ fontSize: 11, lineHeight: 14, opacity: 0.72 }}>
              {localeAwareFormat(new Date(comment.createdAt), 'd MMM, HH:mm')}
            </Text>
          </Column>
        </Row>
        <Text
          marginTop="spacing.general.x1"
          variant="bodyBase"
          style={{ fontSize: 15, lineHeight: 21, color: theme.colors['text.primary'] }}
        >
          {comment.body}
        </Text>
      </Column>
      {isOwnComment ? (
        <Pressable
          accessibilityRole="button"
          hitSlop={15}
          onHoverIn={() => setDeleteHovered(true)}
          onHoverOut={() => setDeleteHovered(false)}
          onPress={() => onDelete(comment.id)}
          style={{
            position: 'absolute',
            top: theme.spacing['spacing.general.x1'],
            right: theme.spacing['spacing.general.x1'],
            padding: 6,
            borderRadius: 999,
            opacity: deleteHovered ? 0.8 : 0.32,
            backgroundColor: deleteHovered ? theme.colors['surface.default'] : 'transparent',
          }}
        >
          <IconX strokeWidth={ICON_STROKE_WIDTH} size={16} color={theme.colors['text.primary']} />
        </Pressable>
      ) : null}
    </Column>
  );
}

function OthersListingHero({
  allPictures,
  availability,
  hasUnavailabilityBanner,
  heroHeight,
  listing,
  onSharePress,
  posterPartialPostalCode,
  scrollY,
}: {
  allPictures: SwiperImage[];
  availability: ListingAvailabilityPresentation;
  hasUnavailabilityBanner?: boolean;
  heroHeight: number;
  listing: NeighboredListingProps;
  onSharePress: () => Promise<void>;
  posterPartialPostalCode?: string;
  scrollY: SharedValue<number>;
}) {
  const insets = useSafeAreaInsets();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const reduceMotion = useReducedMotion();
  const mediaStyle = useAnimatedStyle(() => {
    if (reduceMotion) return {};

    return {
      transform: [
        {
          translateY: interpolate(
            scrollY.value,
            [-heroHeight, 0, heroHeight],
            [-heroHeight * 0.5, 0, heroHeight * 0.32],
            Extrapolation.CLAMP,
          ),
        },
        {
          scale: interpolate(scrollY.value, [-heroHeight, 0], [2, 1], Extrapolation.CLAMP),
        },
      ],
    };
  }, [heroHeight, reduceMotion]);

  const overlayStyle = useAnimatedStyle(() => ({
    opacity: reduceMotion
      ? scrollY.value < heroHeight * 0.65
        ? 1
        : 0
      : interpolate(scrollY.value, [heroHeight * 0.42, heroHeight * 0.78], [1, 0], Extrapolation.CLAMP),
    transform: reduceMotion
      ? []
      : [
          {
            translateY: interpolate(scrollY.value, [0, heroHeight], [0, heroHeight * 0.14], Extrapolation.CLAMP),
          },
        ],
  }));

  const controlsStyle = useAnimatedStyle(() => ({
    opacity: interpolate(scrollY.value, [heroHeight * 0.58, heroHeight * 0.78], [1, 0], Extrapolation.CLAMP),
  }));

  const showHeroOverlay = activeImageIndex === 0;
  const heroTopOffset = hasUnavailabilityBanner
    ? theme.spacing['spacing.general.x2']
    : insets.top + theme.spacing['spacing.general.x2'];

  return (
    <View style={[styles.heroContainer, { height: heroHeight }]}>
      <Animated.View style={[{ height: heroHeight }, mediaStyle]}>
        <ListingHero
          {...listing}
          allPicturesProp={allPictures}
          borderRadius={0}
          height={heroHeight}
          onImageIndexChange={setActiveImageIndex}
          paginationTop={showHeroOverlay ? heroTopOffset : undefined}
          paginationBottom={!showHeroOverlay ? heroTopOffset : undefined}
          hideCorpoBadge
        />
      </Animated.View>
      <HeroCorporateBadge listing={listing} visible={showHeroOverlay} />

      {showHeroOverlay ? (
        <Gradient from="#000000" fromOpacity={0} to="#000000" toOpacity={0.88} style={styles.heroGradient} />
      ) : null}

      <HeroControls
        controlsStyle={controlsStyle}
        listing={listing}
        onSharePress={onSharePress}
        top={heroTopOffset}
        visible={showHeroOverlay}
      />

      <HeroInformation
        availability={availability}
        listing={listing}
        overlayStyle={overlayStyle}
        posterPartialPostalCode={posterPartialPostalCode}
        visible={showHeroOverlay}
      />
    </View>
  );
}

export function OthersListingHeader({ listing }: { listing: NeighboredListingProps }) {
  const { onSharePress } = useContext(ListingContext);
  const route = useRoute<'one-listing'>();
  const { height: viewportHeight } = useWindowDimensions();
  const allPictures: SwiperImage[] = (listing.pictures ?? []).flatMap((picture) =>
    picture.url ? [{ id: picture.id, url: picture.url }] : [],
  );
  const heroHeight = Math.min(HERO_MAX_HEIGHT, Math.max(HERO_MIN_HEIGHT, viewportHeight * 0.46));
  const availability = getListingAvailability(listing);

  return (
    <>
      <OthersListingHero
        allPictures={allPictures}
        availability={availability}
        hasUnavailabilityBanner={listing.hasUnavailabilityBanner}
        heroHeight={heroHeight}
        listing={listing}
        onSharePress={onSharePress}
        posterPartialPostalCode={route.params.hideApproximateLocation ? undefined : listing.publicLocation?.label}
        scrollY={listing.scrollY}
      />

      {listing.tabs}
    </>
  );
}

function AnimatedHeroBackControl({
  hasUnavailabilityBanner = false,
  heroHeight,
  scrollY,
  unavailabilityBannerHeight = 0,
}: {
  hasUnavailabilityBanner?: boolean;
  heroHeight: number;
  scrollY: SharedValue<number>;
  unavailabilityBannerHeight?: number;
}) {
  const insets = useSafeAreaInsets();
  const { t } = useTranslation('accessibility');
  const { navigation } = useContext(ListingContext);
  const heroTopOffset = hasUnavailabilityBanner
    ? unavailabilityBannerHeight + theme.spacing['spacing.general.x2']
    : insets.top + theme.spacing['spacing.general.x2'];
  const animatedStyle = useAnimatedStyle(() => ({
    opacity: interpolate(scrollY.value, [heroHeight * 0.58, heroHeight * 0.78], [1, 0], Extrapolation.CLAMP),
  }));

  return (
    <Animated.View
      pointerEvents="box-none"
      style={[styles.fixedHeroBackControl, { top: heroTopOffset }, animatedStyle]}
    >
      <FloatingHeroControl accessibilityLabel={t('goBack')} onPress={navigation.goBack}>
        <IconChevronLeft strokeWidth={ICON_STROKE_WIDTH} size={26} color={theme.colors['text.primary']} />
      </FloatingHeroControl>
    </Animated.View>
  );
}

function CollapsedHeaderContent({ listing }: { listing: Listing }) {
  const insets = useSafeAreaInsets();
  const { t } = useTranslation('accessibility');
  const { navigation } = useContext(ListingContext);
  const ownerName = [listing.user.profile.firstName, listing.user.profile.lastName].filter(Boolean).join(' ');

  return (
    <View
      style={[
        styles.collapsedHeaderContent,
        { paddingTop: insets.top, height: insets.top + COLLAPSED_HEADER_CONTENT_HEIGHT },
      ]}
    >
      <Pressable
        accessibilityLabel={t('goBack')}
        accessibilityRole="button"
        hitSlop={10}
        onPress={navigation.goBack}
        style={styles.collapsedBackButton}
      >
        <IconChevronLeft strokeWidth={ICON_STROKE_WIDTH} size={26} color={theme.colors['text.primary']} />
      </Pressable>
      <PCImage
        height={34}
        width={34}
        source={{ uri: getImageVariantUrl(listing.user.profile.picture, 'mini') }}
        style={{ borderRadius: 17 }}
        useOwnCache
      />

      <Column flex={1}>
        <Text fontWeight="700" numberOfLines={1} variant="bodyBase">
          {listing.item.name}
        </Text>
        <Text color="text.secondary" numberOfLines={1} variant="bodySmall">
          {ownerName}
        </Text>
      </Column>
    </View>
  );
}

function AnimatedCollapsedHeader({
  heroHeight,
  listing,
  scrollY,
}: {
  heroHeight: number;
  listing: Listing;
  scrollY: SharedValue<number>;
}) {
  const insets = useSafeAreaInsets();
  const reduceMotion = useReducedMotion();
  const headerHeight = insets.top + COLLAPSED_HEADER_CONTENT_HEIGHT;
  const threshold = heroHeight - headerHeight;
  const animatedStyle = useAnimatedStyle(() => ({
    opacity: reduceMotion
      ? scrollY.value >= threshold
        ? 1
        : 0
      : interpolate(scrollY.value, [threshold - 24, threshold + 8], [0, 1], Extrapolation.CLAMP),
    transform: [
      {
        translateY: reduceMotion
          ? scrollY.value >= threshold
            ? 0
            : -headerHeight
          : interpolate(scrollY.value, [threshold - 24, threshold + 8], [-headerHeight, 0], Extrapolation.CLAMP),
      },
    ],
  }));

  return (
    <Animated.View pointerEvents="box-none" style={[styles.collapsedHeader, animatedStyle]}>
      <CollapsedHeaderContent listing={listing} />
    </Animated.View>
  );
}

export function OthersListingAnimatedChrome({
  bottomAccessory,
  listing,
}: {
  bottomAccessory?: ReactNode;
  listing: NeighboredListingProps;
}) {
  const { height: viewportHeight } = useWindowDimensions();
  const heroHeight = Math.min(HERO_MAX_HEIGHT, Math.max(HERO_MIN_HEIGHT, viewportHeight * 0.46));

  return (
    <>
      <AnimatedHeroBackControl
        hasUnavailabilityBanner={listing.hasUnavailabilityBanner}
        heroHeight={heroHeight}
        scrollY={listing.scrollY}
        unavailabilityBannerHeight={listing.unavailabilityBannerHeight}
      />

      <AnimatedCollapsedHeader heroHeight={heroHeight} listing={listing} scrollY={listing.scrollY} />
      {bottomAccessory ? (
        <View pointerEvents="box-none" style={styles.bottomAccessory}>
          {bottomAccessory}
        </View>
      ) : null}
    </>
  );
}

function getFullAddress(listing: Listing) {
  if (!listing.address) return undefined;

  return [
    listing.address.street,
    listing.address.unit,
    listing.address.city,
    listing.address.administrativeArea,
    listing.address.postalCode,
  ]
    .filter(Boolean)
    .join(', ');
}

function useListingBorrowerData(listing: Listing) {
  const isGuestWeb = useIsGuestWeb();

  return useListingBorrowers(listing.id, {
    enabled:
      !isGuestWeb && listing.type === ListingType.offer && ((listing.numLoans ?? 0) > 0 || listing.status === 'loaned'),
  });
}

function ListingDescription({ listing }: { listing: Listing }) {
  const { t } = useTranslation('one-listing');
  const { data: user } = useUser();
  const perishableListingsEnabled = isFeatureEnabled(FeatureFlag.perishableListings, user?.featureFlags);
  const hasMeaningfulDescription =
    !isBlank(listing.description ?? '') &&
    listing.description?.trim().toLocaleLowerCase() !== listing.item.name.trim().toLocaleLowerCase();

  return (
    <>
      <SectionHeading>{t('sections.about')}</SectionHeading>
      <Text
        color={hasMeaningfulDescription ? 'text.primary' : 'text.secondary'}
        style={{ lineHeight: 23 }}
        variant="bodyBase"
      >
        {hasMeaningfulDescription ? listing.description : t('sections.noDescription')}
      </Text>
      <ListingTags
        listing={listing}
        showPerishable={listing.subtype === ListingSubtype.expirable && perishableListingsEnabled}
      />
    </>
  );
}

function ListingMetadata({
  listing,
  loanFeeModalsState,
}: {
  listing: Listing;
  loanFeeModalsState: LoanFeeModalsState;
}) {
  const { t } = useTranslation('one-listing');

  return (
    <>
      {listing.quantity && listing.quantity > 1 ? (
        <Text numberOfLines={1} variant="bodyBase">
          <Trans
            components={{ strong: <Text fontWeight="700" /> }}
            i18nKey="headerSection.quantity"
            ns="one-listing"
            values={{ num: listing.quantity.toLocaleString() }}
          />
        </Text>
      ) : undefined}

      <OfferDetailsSection listing={listing} loanFeeModalsState={loanFeeModalsState} />

      {listing.before ? (
        <Text numberOfLines={1} variant="bodyBase">
          {t('headerSection.dateBefore')} :{' '}
          {localeAwareFormat(new Date(listing.before), t('dateFormat_MMMdYYYY', { ns: 'common' }))}
        </Text>
      ) : null}
    </>
  );
}

function ListingAvailabilityAndInsurance({ listing }: { listing: Listing }) {
  const isInsured = Boolean(useInsuredUsers(listing.user, 'listing'));

  return (
    <>
      {listing.type === ListingType.offer &&
      !isWhiteLabel(WhiteLabel.HauteGaspesie) &&
      listing.user.profile.placeDetails?.allowInAppContact !== false ? (
        <ListingAvailabilityCalendar listing={listing} />
      ) : null}

      {isInsured ? (
        <InsuredUser
          showText={false}
          otherUser={listing.user}
          placement="listing"
          style={{
            borderRadius: theme.borderRadii['radius.lg'],
            paddingHorizontal: theme.spacing['spacing.general.x4'],
            paddingVertical: theme.spacing['spacing.general.x2'],
          }}
        />
      ) : null}
    </>
  );
}

function RatingsSection({ listing }: { listing: Listing }) {
  const { t } = useTranslation();
  const { navigation } = useContext(ListingContext);
  const isGuestWeb = useIsGuestWeb();
  const authorizeReviews = useAuthorize('reviews');
  const { mutate: deleteRating } = useDeleteRating();
  const { mutate: reportRating } = useReportRating();
  const { data: user } = useUser();
  const isSaleOrDonation = listing.transactionMode === TransactionMode.sell || isDonationListing(listing);
  const listingRatingsQuery = useRatings({ listingId: listing.id }, { enabled: !isGuestWeb && !isSaleOrDonation });
  const ratingsCount = listingRatingsQuery.data?.length ?? 0;
  const ratingsPreview =
    listingRatingsQuery.data
      ?.filter(
        (rating) =>
          rating.borrowerCommunityTrustRating !== undefined || rating.lenderCommunityTrustRating !== undefined,
      )
      .slice(0, 3) ?? [];

  if (isSaleOrDonation) return null;

  if (ratingsPreview.length > 0) {
    return (
      <Column>
        <SectionHeading>{t('reviews.count', { ns: 'neighbour-profile', count: ratingsCount })}</SectionHeading>
        <FlatList
          data={ratingsPreview}
          horizontal
          keyExtractor={(rating) => rating.loanId}
          renderItem={({ item: rating }) => (
            <RatingCard
              hideTopLine
              rating={rating}
              forcedWidth={260}
              hideListing
              currentUserId={user?.id}
              onEditRating={() => {
                navigation.navigate(
                  'update-rating',
                  {
                    loanId: rating.loanId,
                    listingId: rating.listingId,
                    channelUrl: rating.channelUrl,
                    isLender: rating.reviewerRole === 'lender',
                    subjectName:
                      [rating.ratedUserFirstName, rating.ratedUserLastName].filter(Boolean).join(' ').trim() ||
                      rating.listingTitle ||
                      '',
                    subjectImage: null,
                    overallRating: rating.overallRating,
                    assetQualityRating: rating.assetQualityRating,
                    logisticsReliabilityRating: rating.logisticsReliabilityRating,
                    communityTrustRating: rating.communityTrustRating,
                    comment: rating.comment,
                    initialExperience: (rating.overallRating ?? 0) >= 3 ? 'positive' : 'negative',
                  },
                  { pop: true },
                );
              }}
              onDeleteRating={() => {
                Alert.alert(
                  t('reviews.actions.deleteConfirmTitle', { ns: 'neighbour-profile' }),
                  t('reviews.actions.deleteConfirmMessage', { ns: 'neighbour-profile' }),
                  [
                    {
                      text: t('reviews.actions.deleteConfirmCancel', { ns: 'neighbour-profile' }),
                      style: 'cancel',
                    },
                    {
                      text: t('reviews.actions.deleteConfirmOk', { ns: 'neighbour-profile' }),
                      style: 'destructive',
                      onPress: () => deleteRating(rating.loanId),
                    },
                  ],
                );
              }}
              onReportRating={() => {
                reportRating(rating.loanId, {
                  onSuccess: () => {
                    navigateToChat(navigation, SUPPORT_CHANNEL_URL, {
                      i18nKey: 'reportRatingPrompt',
                      ns: 'neighbour-profile',
                    });
                  },
                });
              }}
            />
          )}
          showsHorizontalScrollIndicator={false}
          style={styles.horizontalCardList}
          contentContainerStyle={{ gap: theme.spacing['spacing.general.x3'] }}
        />
      </Column>
    );
  }

  if (listing.type !== ListingType.offer) return null;

  const ratingsLabel = t('reviews.viewAllCount', { ns: 'neighbour-profile', count: ratingsCount });
  const ratingsSubjectName =
    [listing.user.profile.firstName, listing.user.profile.lastName].filter(Boolean).join(' ').trim() ||
    listing.item.name;

  return (
    <Pressable
      onPress={() =>
        authorizeReviews(() =>
          navigation.navigate(
            'ratings',
            {
              listingId: listing.id,
              userId: listing.userId,
              subjectName: ratingsSubjectName,
              listingTitle: listing.item.name,
            },
            { pop: true },
          ),
        )
      }
      style={[styles.actionCard, { backgroundColor: theme.colors['surface.brand.secondary-5.subtle'] }]}
    >
      <Row alignItems="center" gap="spacing.general.x3">
        <View style={styles.actionCardIcon}>
          <Text fontSize={20}>{THUMBS_UP}</Text>
        </View>
        <Text flex={1} fontSize={15} fontWeight="600" lineHeight={20} numberOfLines={1} color="text.primary">
          {ratingsLabel}
        </Text>
        <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} size={28} color={theme.colors['icon.primary']} />
      </Row>
    </Pressable>
  );
}

function WantToHelpCard({ listing, lenders }: { listing: Listing; lenders: NonNullable<Listing['lenders']> }) {
  const { t } = useTranslation('one-listing');
  const { navigation } = useContext(ListingContext);
  const totalLendersCount = lenders.length + (listing.additionalLendersCount ?? 0);

  return (
    <Pressable
      accessibilityRole="button"
      onPress={() => {
        navigation.navigate(
          'listing-activity',
          {
            listingId: listing.id,
            isLended: Boolean(listing.lender),
          },
          { pop: true },
        );
      }}
      style={styles.actionCard}
    >
      <Row alignItems="center" gap="spacing.general.x3">
        <Row alignItems="center" style={{ paddingLeft: theme.spacing['spacing.general.x1'] }}>
          {lenders.slice(0, 5).map((lender, index) => (
            <View key={lender.id} style={{ marginLeft: index === 0 ? 0 : -10, zIndex: 5 - index }}>
              <PCImage
                height={44}
                width={44}
                source={lender.picture ? { uri: lender.picture } : undefined}
                style={{
                  backgroundColor: theme.colors['surface.secondary'],
                  borderColor:
                    listing.lender?.id === lender.id ? theme.colors['text.positive'] : theme.colors['surface.default'],
                  borderRadius: 22,
                  borderWidth: 2,
                }}
                useOwnCache
              />
              {listing.lender?.id === lender.id ? (
                <CheckCircle2
                  color={theme.colors['surface.success.subtle']}
                  fillColor={theme.colors['text.positive']}
                  height={14}
                  width={14}
                  style={{ bottom: -1, position: 'absolute', right: 0 }}
                />
              ) : null}
            </View>
          ))}
          {listing.additionalLendersCount && listing.additionalLendersCount > 0 ? (
            <Row
              alignItems="center"
              height={44}
              justifyContent="center"
              style={{
                backgroundColor: theme.colors['surface.tertiary-neutral'],
                borderColor: theme.colors['surface.default'],
                borderRadius: 22,
                borderWidth: 2,
                marginLeft: -10,
                minWidth: 44,
                paddingHorizontal: theme.spacing['spacing.general.x1'],
              }}
            >
              <Text color="text.secondary" fontWeight="700" variant="bodySmall">
                +{listing.additionalLendersCount}
              </Text>
            </Row>
          ) : null}
        </Row>

        <Column flex={1} gap="spacing.general.x0,5">
          <Text fontWeight="700" variant="bodyBase">
            {t('wantToHelpTitle', { count: totalLendersCount })}
          </Text>
          <Text color="text.secondary" variant="bodySmall">
            {t('wantToHelpSubtitle')}
          </Text>
        </Column>
        <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} size={22} color={theme.colors['icon.tertiary']} />
      </Row>
    </Pressable>
  );
}

function BeTheFirstToHelpCard() {
  const { t, isAuthor } = useContext(ListingContext);

  return (
    <Row alignItems="center" gap="spacing.general.x3" style={styles.actionCard}>
      <Row
        alignItems="center"
        height={44}
        justifyContent="center"
        style={{ backgroundColor: theme.colors['surface.secondary'], borderRadius: 22, width: 44 }}
      >
        <IconUsers strokeWidth={ICON_STROKE_WIDTH} size={22} color={theme.colors['text.brand']} />
      </Row>
      <Column flex={1} gap="spacing.general.x0,5">
        <Text fontWeight="700" variant="bodyBase">
          {t('beTheFirstToHelpTitle')}
        </Text>
        {!isAuthor ? (
          <Text color="text.secondary" variant="bodySmall">
            {t('beTheFirstToHelp')}
          </Text>
        ) : null}
      </Column>
    </Row>
  );
}

function RequestHelpersSection({ listing }: { listing: Listing }) {
  const lenders = listing.lenders ?? [];
  if (listing.type !== ListingType.request) return null;

  if (lenders.length > 1 || (lenders.length === 1 && !listing.lender)) {
    return <WantToHelpCard listing={listing} lenders={lenders} />;
  }

  if (!listing.lender && lenders.length === 0) return <BeTheFirstToHelpCard />;

  return null;
}

function BorrowersSection({ borrowers, listing }: { borrowers: ListingBorrower[]; listing: Listing }) {
  const { t } = useTranslation('one-listing');
  if (borrowers.length === 0) return null;

  return (
    <Column>
      <SectionHeading>{t('borrowers.title', { count: listing.numLoans ?? borrowers.length })}</SectionHeading>
      <FlatList
        contentContainerStyle={styles.borrowersListContent}
        data={borrowers}
        horizontal
        keyExtractor={(borrower) => `${borrower.id}-${borrower.lastBorrowedAt}`}
        renderItem={({ item: borrower }) => <BorrowerCard borrower={borrower} />}
        showsHorizontalScrollIndicator={false}
        style={styles.borrowersList}
      />
    </Column>
  );
}

function ListingTimeDetails({ listing }: { listing: Listing }) {
  if (!listing.timeStart && !listing.timeEnd) return null;

  return (
    <Text numberOfLines={1} variant="bodyBase">
      <Trans
        components={{ strong: <Text fontWeight="700" /> }}
        i18nKey={
          listing.timeStart && listing.timeEnd
            ? 'headerSection.timeBoth'
            : listing.timeStart
              ? 'headerSection.timeStart'
              : 'headerSection.timeEnd'
        }
        ns="one-listing"
        values={{ start: listing.timeStart, end: listing.timeEnd }}
      />
    </Text>
  );
}

function ListingAddress({ fullAddress }: { fullAddress?: string }) {
  const { t } = useTranslation('one-listing');
  if (!fullAddress) return null;

  const openMap = () => {
    // @TODO improve this by using https://github.com/tschoffelen/react-native-map-link/blob/master/docs/expo.md
    // Requires a new build...
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
    Linking.openURL(url).catch((err: unknown) => console.error('Error opening map: ', err));
  };

  return (
    <Pressable
      accessibilityRole="link"
      onPress={openMap}
      style={{ paddingVertical: theme.spacing['spacing.general.x1'] }}
    >
      <Row>
        <Text variant="bodyBase">{t('headerSection.address')} : </Text>
        <Text flex={1} fontWeight="700" textDecorationLine="underline" variant="bodyBase">
          {fullAddress}
        </Text>
      </Row>
    </Pressable>
  );
}

function ListingApproximateLocation({ listing, hidden }: { listing: Listing; hidden: boolean }) {
  const { t } = useTranslation('one-listing');
  if (!listing.publicLocation || hidden) return null;

  const listingKind = listing.type === ListingType.request ? 'Borrower' : 'Pickup';

  return (
    <ApproximateLocationMap
      actionLabel={t('headerSection.viewMap')}
      compact
      location={listing.publicLocation}
      locations={listing.publicLocations}
      title={t(`headerSection.approximate${listingKind}Area`, { count: listing.publicLocations?.length ?? 1 })}
      hint={t(
        listing.type === ListingType.request
          ? 'headerSection.approximateBorrowerLocationHint'
          : 'headerSection.approximateLocationHint',
      )}
      privacyMessage={t(
        listing.type === ListingType.request
          ? 'headerSection.approximateBorrowerLocationSecurity'
          : 'headerSection.approximateLocationSecurity',
      )}
    />
  );
}

function PickupDetailsSection({ listing }: { listing: Listing }) {
  const route = useRoute<'one-listing'>();
  const isGuestWeb = useIsGuestWeb();
  const fullAddress = getFullAddress(listing);
  const hasPickupDetails = Boolean(
    listing.timeStart ||
    listing.timeEnd ||
    fullAddress ||
    (listing.publicLocation && !isGuestWeb && !route.params.hideApproximateLocation),
  );
  if (!hasPickupDetails) return null;

  return (
    <Column gap="spacing.general.x2">
      <ListingTimeDetails listing={listing} />
      <ListingAddress fullAddress={fullAddress} />
      <ListingApproximateLocation
        listing={listing}
        hidden={isGuestWeb || Boolean(route.params.hideApproximateLocation)}
      />
    </Column>
  );
}

function ParticipationButtons({
  isParticipating,
  isPending,
  listingType,
  numParticipants,
  numParticipantsLeft,
  onParticipate,
}: {
  isParticipating: boolean;
  isPending: boolean;
  listingType: ListingType;
  numParticipants: number;
  numParticipantsLeft: number;
  onParticipate: (count: number) => void;
}) {
  const { t } = useTranslation('one-listing');

  return (
    <>
      <Button
        authorize
        flex={1}
        disabled={!isParticipating && numParticipantsLeft <= 0}
        justifyContent="center"
        loading={isPending}
        marginTop={listingType === ListingType.request ? 'spacing.general.x8' : 'spacing.general.x4'}
        minHeight={48}
        onPress={() => onParticipate(numParticipants)}
        variant="primary"
      >
        <Text color="text.inverse.primary" fontWeight="700" textAlign="center" variant="bodyBase">
          {t(
            isParticipating
              ? 'primaryButtonText'
              : numParticipantsLeft <= 0
                ? 'cantParticipateButtonText'
                : 'participateButtonText',
          )}
        </Text>
      </Button>

      {isParticipating ? (
        <Button
          authorize
          flex={1}
          justifyContent="center"
          loading={isPending}
          marginTop="spacing.general.x4"
          minHeight={48}
          onPress={() => onParticipate(0)}
          variant="secondary"
        >
          <Text fontWeight="700" textAlign="center" variant="bodyBase">
            {t('unparticipateButtonText')}
          </Text>
        </Button>
      ) : null}
    </>
  );
}

function ParticipationSection({ listing }: { listing: Listing }) {
  const numParticipantsInputRef = useRef<TextInput>(null);
  const { t, participateButtonHandler, createParticipation, isAuthor } = useContext(ListingContext);
  const { t: tAPI } = useTranslation('api-errors');
  const [numNewParticipantsState, setNumNewParticipantsState] = useState({
    source: listing.myParticipants,
    value: listing.myParticipants ?? 1,
  });
  let currentNumNewParticipantsState = numNewParticipantsState;
  if (numNewParticipantsState.source !== listing.myParticipants) {
    currentNumNewParticipantsState = {
      source: listing.myParticipants,
      value: listing.myParticipants ?? 1,
    };
    setNumNewParticipantsState(currentNumNewParticipantsState);
  }

  const hasMaxParticipants = listing.maxParticipants !== undefined && listing.maxParticipants !== null;
  if (!hasMaxParticipants || isAuthor) return null;

  const numNewParticipants = currentNumNewParticipantsState.value;
  const numParticipantsLeft = (listing.maxParticipants ?? 0) - (listing.numParticipants ?? 0);
  const isParticipating = Boolean(listing.myParticipants && listing.myParticipants > 0);
  const setNumNewParticipants = (value: number) => {
    setNumNewParticipantsState({ source: listing.myParticipants, value });
  };

  return (
    <Column>
      <View
        style={{
          backgroundColor: theme.colors['surface.tertiary-neutral'],
          borderRadius: theme.borderRadii['radius.xl'],
          paddingHorizontal: theme.spacing['spacing.general.x5'],
          paddingVertical: theme.spacing['spacing.general.x6'],
        }}
      >
        <Text fontWeight="700" textAlign="center" variant="bodyBase">
          {t('subscriptionsSection.titlePublic')}
        </Text>
        <Pressable
          onPress={() => numParticipantsInputRef.current?.focus()}
          style={{
            backgroundColor: theme.colors['surface.default'],
            borderRadius: theme.borderRadii['radius.lg'],
            marginTop: theme.spacing['spacing.general.x4'],
            padding: theme.spacing['spacing.general.x4'],
          }}
        >
          <Row alignItems="center" gap="spacing.general.x4" justifyContent="space-between">
            <Column gap="spacing.general.x1" style={{ maxWidth: '75%' }}>
              <Text fontWeight="700" variant="bodyBase">
                {t('subscriptionsSection.textPublic')}
              </Text>
              <Row style={{ flexShrink: 1 }}>
                <Text color="text.secondary">
                  {t('subscriptionsSection.participantsLeft', {
                    ns: 'one-listing',
                    count: numParticipantsLeft,
                    max: listing.maxParticipants,
                  })}
                </Text>
              </Row>
            </Column>
            <Column flex={1}>
              <Input
                autoWidth
                innerRef={numParticipantsInputRef}
                keyboardType="number-pad"
                onChangeText={(newValue) => setNumNewParticipants(Number.parseInt(newValue))}
                textAlign="center"
                value={Number.isInteger(numNewParticipants) ? numNewParticipants.toFixed(0) : ''}
              />
            </Column>
          </Row>
        </Pressable>
        {createParticipation.error ? (
          <View style={{ marginTop: theme.spacing['spacing.general.x4'] }}>
            <RemoteError {...{ error: createParticipation.error, message: tAPI(createParticipation.error.message) }} />
          </View>
        ) : null}
      </View>

      <ParticipationButtons
        isParticipating={isParticipating}
        isPending={createParticipation.isPending}
        listingType={listing.type}
        numParticipants={numNewParticipants}
        numParticipantsLeft={numParticipantsLeft}
        onParticipate={participateButtonHandler}
      />
    </Column>
  );
}

function CommentsToggleIcon({ expanded, hasComments }: { expanded?: boolean; hasComments: boolean }) {
  if (!hasComments) return null;
  const Icon = expanded ? IconChevronUp : IconChevronDown;
  return <Icon strokeWidth={ICON_STROKE_WIDTH} size={18} color={theme.colors['text.primary']} />;
}

function ExpandedComments({
  comments,
  expanded,
  onDelete,
}: {
  comments: ListingComment[];
  expanded?: boolean;
  onDelete: (commentId: string) => void;
}) {
  const { data: user } = useUser();
  if (comments.length === 0 || !expanded) return null;

  return (
    <Column gap="spacing.general.x8" backgroundColor="surface.tertiary-neutral" borderRadius="radius.lg">
      <Column gap="spacing.general.x2" marginTop="spacing.general.x2">
        {comments.map((comment, index) => (
          <CommentThreadItem
            key={comment.id}
            comment={comment}
            isOwnComment={user?.id === comment.user.id}
            onDelete={onDelete}
            withBorderBottom={comments.length === 1 || index === comments.length - 1}
          />
        ))}
      </Column>
    </Column>
  );
}

function CommentComposer({
  inputRef,
  isCreating,
  listing,
  newCommentText,
  onChangeText,
  onSubmit,
}: {
  inputRef: RefObject<TextInput | null>;
  isCreating: boolean;
  listing: NeighboredListingProps;
  newCommentText: string;
  onChangeText: (value: string) => void;
  onSubmit: () => void;
}) {
  const { t: tComments } = useTranslation('listing-comments');
  const { data: user } = useUser();
  const canSubmit = Boolean(newCommentText.trim());

  if (!user) {
    return (
      <View
        style={{
          backgroundColor: theme.colors['surface.tertiary-neutral'],
          borderRadius: theme.borderRadii['radius.xl'],
          borderWidth: 1,
          borderColor: theme.colors['border.secondary'],
          paddingHorizontal: theme.spacing['spacing.general.x4'],
          paddingVertical: theme.spacing['spacing.general.x3'],
        }}
      >
        <Text variant="bodyBase" color="text.primary">
          {tComments('loginToComment')}
        </Text>
      </View>
    );
  }

  return (
    <Input
      innerRef={inputRef}
      multiline
      onBlur={() => listing.onCommentInputBlur?.()}
      onFocus={() => listing.onCommentInputFocus?.(inputRef.current)}
      onChangeText={onChangeText}
      placeholder={tComments('placeholder')}
      style={{
        minHeight: COMMENT_MESSAGE_MIN_HEIGHT,
        color: theme.colors['text.primary'],
        fontSize: 15,
        lineHeight: 20,
        paddingVertical: theme.spacing['spacing.general.x2'],
        textAlignVertical: 'center',
      }}
      value={newCommentText}
      right={
        <Pressable
          disabled={!canSubmit || isCreating}
          hitSlop={6}
          onPress={onSubmit}
          style={{
            width: 36,
            height: 36,
            borderRadius: 18,
            alignItems: 'center',
            justifyContent: 'center',
            marginVertical: theme.spacing['spacing.general.x1'],
            backgroundColor: canSubmit
              ? theme.colors['text.primary']
              : theme.colors['surface.inverse.tertiary-neutral'],
            opacity: isCreating ? 0.7 : 1,
          }}
        >
          <IconSend strokeWidth={ICON_STROKE_WIDTH} size={18} color={theme.colors['surface.default']} />
        </Pressable>
      }
    />
  );
}

function ListingCommentsSection({ listing }: { listing: NeighboredListingProps }) {
  const commentInputRef = useRef<TextInput>(null);
  const { t: tComments } = useTranslation('listing-comments');
  const isRequestListing = listing.type === ListingType.request;
  const { data: comments = [] } = useListingComments(listing.id, {
    enabled: isRequestListing && COMMENTS_ENABLED,
  });
  const { mutate: createComment, isPending: isCreatingComment } = useCreateListingComment(listing.id);
  const { mutate: deleteComment } = useDeleteListingComment(listing.id);
  const [expandedComments, setExpandedComments] = useState<boolean>();
  const [newCommentText, setNewCommentText] = useState('');
  const hasComments = comments.length > 0;
  const handleSubmitComment = () => {
    const trimmed = newCommentText.trim();
    if (!trimmed) return;
    createComment(trimmed, {
      onSuccess: () => {
        setNewCommentText('');
        setExpandedComments(true);
      },
    });
  };

  // Disable comments section until Hugo comes up with a better UI/UX.

  if (!isRequestListing || !showCommentsUI) return null;

  return (
    <Column gap="spacing.general.x4" marginTop="spacing.general.x4">
      <Pressable onPress={() => setExpandedComments((previous) => !previous)}>
        <Row justifyContent="space-between" alignItems="center">
          <Row alignItems="center" gap="spacing.general.x1">
            <Text fontWeight="600" textAlign="center" variant="bodyBase">
              💭
            </Text>
            <Text variant="bodyLarge" fontWeight="700">
              {tComments('title', { count: comments.length })}
            </Text>
          </Row>
          <CommentsToggleIcon expanded={expandedComments} hasComments={hasComments} />
        </Row>
      </Pressable>

      <ExpandedComments comments={comments} expanded={expandedComments} onDelete={deleteComment} />
      <CommentComposer
        inputRef={commentInputRef}
        isCreating={isCreatingComment}
        listing={listing}
        newCommentText={newCommentText}
        onChangeText={setNewCommentText}
        onSubmit={handleSubmitComment}
      />
    </Column>
  );
}

function RecommendationsSection({ listing }: { listing: Listing }) {
  const isLargeUI = useUISize().isLargeUI;
  const runtime = useRecommendationRuntime();
  const recommendationState = useSimilarListingsRecommendationsWithRuntime(
    {
      strategy: Strategies.UseOneToManySimilarListings,
      recommendationType: listing.type,
      similarityType: FeatureType.CONTENT,
      listingId: listing.id,
      top_n: 1,
    },
    runtime,
  );
  const { recommendations } = recommendationState;
  const RecommendationWrapper = isLargeUI ? DesktopRecommendationSection<Listing> : ListingListWithStrategy;
  if (!recommendations?.length) return null;

  return (
    <Column>
      <Separator
        onlyWidth
        color={theme.colors['border.secondary']}
        style={{ marginBottom: theme.spacing['spacing.general.x3'] }}
      />
      <RecommendationWrapper
        recommendationState={recommendationState}
        strategyProps={{
          strategy: Strategies.UseOneToManySimilarListings,
          recommendationType: listing.type,
          similarityType: FeatureType.CONTENT,
          listingId: listing.id,
          top_n: 1,
        }}
        recommendations={recommendations as Listing[]}
        renderItem={(item) =>
          listing.type === ListingType.offer ? (
            <ListingOfferCard listing={item as Listing} />
          ) : (
            <ListingRequestCard showUser listing={item as Listing} />
          )
        }
      />
    </Column>
  );
}

function ReportListingButton({
  disabled,
  isAuthor,
  onPress,
}: {
  disabled: boolean;
  isAuthor: boolean;
  onPress: () => void;
}) {
  const { t } = useTranslation('one-listing');
  if (isAuthor) return null;

  return (
    <Pressable disabled={disabled} onPress={onPress} style={styles.reportListingButton}>
      <Column alignItems="center">
        <Row alignItems="center" gap="spacing.general.x2">
          <IconAlertTriangle strokeWidth={ICON_STROKE_WIDTH} size={20} color={theme.colors['surface.error.default']} />
          <Text variant="bodySmall" color="surface.error.default">
            {t('reportListing')}
          </Text>
        </Row>
      </Column>
    </Pressable>
  );
}

function ReportListingModal({
  onClose,
  onConfirm,
  open,
}: {
  onClose: () => void;
  onConfirm: () => void;
  open: boolean;
}) {
  const { t } = useTranslation('one-listing');

  return (
    <BottomSheet onClose={onClose} open={open}>
      <BottomSheet.Action
        onPress={onConfirm}
        prefix={<IconAlertTriangle strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['surface.error.default']} />}
      >
        <Text color="surface.error.default">{t('reportListing')}</Text>
      </BottomSheet.Action>
    </BottomSheet>
  );
}

function OthersListingDetails({
  borrowers,
  isAuthor,
  isReportingListing,
  listing,
  loanFeeModalsState,
  onReportListing,
}: {
  borrowers: ListingBorrower[];
  isAuthor: boolean;
  isReportingListing: boolean;
  listing: NeighboredListingProps;
  loanFeeModalsState: LoanFeeModalsState;
  onReportListing: () => void;
}) {
  const { t } = useTranslation('one-listing');
  const currentBorrower = borrowers.find((borrower) => borrower.isCurrent);

  return (
    <Column gap="spacing.general.x3" style={styles.detailsContent}>
      <Column gap="spacing.general.x3">
        <ListingDescription listing={listing} />
        <ListingMetadata listing={listing} loanFeeModalsState={loanFeeModalsState} />
        {currentBorrower ? <CurrentBorrowerSection borrower={currentBorrower} /> : null}
        <ListingAvailabilityAndInsurance listing={listing} />
        <RatingsSection listing={listing} />
        <RequestHelpersSection listing={listing} />
        {listing.lender ? <LenderAcceptedLoan lender={listing.lender} t={t} /> : null}
        <BorrowersSection borrowers={borrowers} listing={listing} />
        <PickupDetailsSection listing={listing} />
      </Column>

      <ParticipationSection listing={listing} />
      <ListingCommentsSection listing={listing} />
      <RecommendationsSection listing={listing} />
      <ReportListingButton disabled={isReportingListing} isAuthor={isAuthor} onPress={onReportListing} />
    </Column>
  );
}

export const OthersListing: React.FC<NeighboredListingProps> = (listing) => {
  const authorizeReport = useAuthorize('report');
  const { isAuthor, navigation } = useContext(ListingContext);
  const { data: borrowers = [] } = useListingBorrowerData(listing);
  const { mutate: reportListing, isPending: isReportingListing } = useReportListing();
  const [showReportConfirmation, setShowReportConfirmation] = useState(false);
  const loanFeeModalsState = useLoanFeeModals();
  const insets = useSafeAreaInsets();
  const { scrollOffsetRef, scrollViewRef, setPageRef } = listing;
  const bottomInset = listing.bottomAccessory
    ? insets.bottom + theme.spacing['spacing.general.x20']
    : insets.bottom + theme.spacing['spacing.general.x8'];
  const contentInset = Platform.OS === 'ios' ? { bottom: bottomInset } : undefined;
  const contentContainerStyle = Platform.OS === 'ios' ? undefined : { paddingBottom: bottomInset };
  const assignScrollRef = (ref: ScrollView | null) => {
    if (scrollViewRef) {
      scrollViewRef.current = ref;
    }
    setPageRef?.(ref);
  };

  const rememberScrollOffset = (offset: number) => {
    if (scrollOffsetRef) {
      scrollOffsetRef.current = offset;
    }
  };

  return (
    <>
      <Animated.ScrollView
        ref={assignScrollRef}
        contentContainerStyle={contentContainerStyle}
        contentInset={contentInset}
        onMomentumScrollEnd={(event) => rememberScrollOffset(event.nativeEvent.contentOffset.y)}
        onScroll={listing.onScroll}
        onScrollEndDrag={(event) => rememberScrollOffset(event.nativeEvent.contentOffset.y)}
        refreshControl={listing.refreshControl}
        scrollIndicatorInsets={contentInset}
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={false}
        style={styles.scrollView}
        testID="one-listing-screen"
      >
        <OthersListingHeader listing={listing} />
        {listing.topContent}

        <OthersListingDetails
          borrowers={borrowers}
          isAuthor={isAuthor}
          isReportingListing={isReportingListing}
          listing={listing}
          loanFeeModalsState={loanFeeModalsState}
          onReportListing={() => authorizeReport(() => setShowReportConfirmation(true))}
        />
      </Animated.ScrollView>

      <ReportListingModal
        onClose={() => setShowReportConfirmation(false)}
        onConfirm={() => {
          setShowReportConfirmation(false);
          reportListing(listing.id, {
            onSuccess: () => {
              navigateToChat(navigation, SUPPORT_CHANNEL_URL, {
                i18nKey: 'reportListingPrompt',
                ns: 'one-listing',
              });
            },
          });
        }}
        open={showReportConfirmation}
      />
      <LoanFeeModals state={loanFeeModalsState} />
      <ToastProvider />
    </>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
    minHeight: 0,
  },
  detailsContent: {
    paddingHorizontal: theme.spacing['spacing.general.x3'],
    paddingTop: theme.spacing['spacing.general.x3'],
  },
  listingTag: {
    backgroundColor: theme.colors['surface.tertiary-neutral'],
    borderRadius: theme.borderRadii['radius.xl'],
    maxWidth: '100%',
    paddingHorizontal: theme.spacing['spacing.general.x2'],
    paddingVertical: theme.spacing['spacing.general.x1'],
  },
  requestDateTag: {
    backgroundColor: theme.colors['surface.tertiary-neutral'],
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: theme.borderRadii['radius.sm'],
  },
  detailCard: {
    marginTop: theme.spacing['spacing.general.x2'],
    marginBottom: theme.spacing['spacing.general.x3'],
  },
  detailCardSeparator: {
    marginVertical: theme.spacing['spacing.general.x3'],
  },
  offerDetailIcon: {
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: theme.borderRadii['radius.full'],
    backgroundColor: theme.colors['surface.tertiary-neutral'],
  },
  borrowersList: {
    marginTop: theme.spacing['spacing.general.x3'],
  },
  borrowersListContent: {
    gap: theme.spacing['spacing.general.x3'],
  },
  borrowerCard: {
    alignItems: 'center',
    gap: theme.spacing['spacing.general.x1'],
    width: 76,
  },
  borrowerAvatar: {
    backgroundColor: theme.colors['surface.secondary'],
    borderColor: theme.colors['surface.default'],
    borderWidth: 2,
  },
  borrowerBadge: {
    position: 'absolute',
  },
  currentBorrowerSection: {
    alignItems: 'center',
    backgroundColor: theme.colors['surface.default'],
    borderBottomColor: theme.colors['border.secondary'],
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderTopColor: theme.colors['border.secondary'],
    borderTopWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    gap: theme.spacing['spacing.general.x3'],
    marginHorizontal: -theme.spacing['spacing.general.x3'],
    minHeight: 94,
    paddingHorizontal: theme.spacing['spacing.general.x4'],
    paddingVertical: theme.spacing['spacing.general.x3'],
  },
  currentBorrowerSectionPressed: {
    backgroundColor: theme.colors['surface.tertiary-neutral'],
  },
  currentBorrowerDetails: {
    flex: 1,
    gap: theme.spacing['spacing.general.x0,5'],
  },
  currentBorrowerChevron: {
    alignItems: 'center',
    backgroundColor: theme.colors['surface.tertiary-neutral'],
    borderRadius: 18,
    height: 36,
    justifyContent: 'center',
    width: 36,
  },
  actionCard: {
    minHeight: 56,
    justifyContent: 'center',
    backgroundColor: theme.colors['surface.default'],
    borderColor: theme.colors['border.secondary'],
    borderRadius: theme.borderRadii['radius.lg'],
    borderWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: theme.spacing['spacing.general.x4'],
    paddingVertical: theme.spacing['spacing.general.x2'],
  },
  actionCardIcon: {
    alignItems: 'center',
    backgroundColor: theme.colors['surface.tertiary-neutral'],
    borderRadius: 18,
    height: 36,
    justifyContent: 'center',
    width: 36,
  },
  horizontalCardList: {
    // marginVertical: theme.spacing['spacing.general.x1'],
  },
  reportListingButton: {
    marginBottom: theme.spacing['spacing.general.x2'],
    marginTop: theme.spacing['spacing.general.x1'],
    paddingVertical: theme.spacing['spacing.general.x2'],
  },
  heroContainer: {
    width: '100%',
    overflow: 'hidden',
    backgroundColor: theme.colors['surface.disabled'],
  },
  heroGradient: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '72%',
  },
  heroControls: {
    position: 'absolute',
    left: theme.spacing['spacing.general.x4'],
    right: theme.spacing['spacing.general.x4'],
    zIndex: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  fixedHeroBackControl: {
    position: 'absolute',
    left: theme.spacing['spacing.general.x4'],
    zIndex: 20,
  },
  floatingControl: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.94)',
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.14)',
  },
  favouriteControlWithCount: {
    minWidth: 62,
    paddingHorizontal: theme.spacing['spacing.general.x2'],
    width: 'auto',
  },
  heroInformation: {
    position: 'absolute',
    left: theme.spacing['spacing.general.x6'],
    right: theme.spacing['spacing.general.x6'],
    bottom: theme.spacing['spacing.general.x5'],
    gap: theme.spacing['spacing.general.x2'],
    zIndex: 10,
  },
  heroTitle: {
    color: '#FFFFFF',
    fontSize: 30,
    lineHeight: 34,
    textShadowColor: 'rgba(0,0,0,0.35)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 4,
  },
  heroSecondaryText: {
    opacity: 0.84,
    textShadowColor: 'rgba(0,0,0,0.35)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 3,
  },
  heroAvatar: {
    borderRadius: 20,
    borderColor: 'rgba(255,255,255,0.9)',
    borderWidth: 1.5,
  },
  availabilityChip: {
    alignSelf: 'flex-start',
    minHeight: 30,
    maxWidth: '100%',
    borderRadius: theme.borderRadii['radius.full'],
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['spacing.general.x1'],
    paddingHorizontal: theme.spacing['spacing.general.x3'],
    paddingVertical: theme.spacing['spacing.general.x1'],
  },
  availabilityDetails: {
    alignSelf: 'stretch',
    justifyContent: 'center',
    borderLeftColor: theme.colors['border.secondary'],
    borderLeftWidth: StyleSheet.hairlineWidth,
    marginVertical: -theme.spacing['spacing.general.x1'],
    paddingLeft: theme.spacing['spacing.general.x1'],
    marginLeft: theme.spacing['spacing.general.x1'],
  },
  collapsedHeader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 30,
    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08)',
  },
  staticHeader: {
    backgroundColor: theme.colors['surface.default'],
    borderBottomColor: theme.colors['border.secondary'],
    borderBottomWidth: 1,
  },
  collapsedHeaderContent: {
    paddingHorizontal: theme.spacing['spacing.general.x4'],
    backgroundColor: theme.colors['surface.default'],
    borderBottomColor: theme.colors['border.secondary'],
    borderBottomWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['spacing.general.x3'],
  },
  collapsedBackButton: {
    width: 44,
    height: 44,
    marginLeft: -theme.spacing['spacing.general.x2'],
    alignItems: 'center',
    justifyContent: 'center',
  },
  bottomAccessory: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 25,
  },
});
